import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  alpha,
  CircularProgress,
  TablePagination,
  Tabs,
  Tab,
  InputAdornment,
  Popover,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import PetsIcon from '@mui/icons-material/Pets';
import FoodBankIcon from '@mui/icons-material/FoodBank';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import EmojiSymbolsIcon from '@mui/icons-material/EmojiSymbols';
import FlagIcon from '@mui/icons-material/Flag';
import PanToolIcon from '@mui/icons-material/PanTool';
import PublicIcon from '@mui/icons-material/Public';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { db } from '../firebase';
import { collection, doc, getDocs, setDoc, deleteDoc, updateDoc, query, where, writeBatch } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import dataByGroup from 'unicode-emoji-json/data-by-group.json';

// Map Unicode group names to MUI icons for the emoji picker tabs
const GROUP_ICONS = {
  'Smileys & Emotion': EmojiEmotionsIcon,
  'People & Body': PanToolIcon,
  'Animals & Nature': PetsIcon,
  'Food & Drink': FoodBankIcon,
  'Travel & Places': PublicIcon,
  'Activities': SportsBasketballIcon,
  'Objects': EmojiObjectsIcon,
  'Symbols': EmojiSymbolsIcon,
  'Flags': FlagIcon,
};

// Build emoji categories from full Unicode RGI emoji set (all ~3700+ emojis)
const emojiCategories = dataByGroup.map((group) => ({
  name: group.name,
  icon: React.createElement(GROUP_ICONS[group.name] || EmojiEmotionsIcon, {}),
  emojis: group.emojis.map((e) => ({ emoji: e.emoji, name: e.name || '', slug: e.slug || '' })),
}));

const UserManagement = () => {
  const navigate = useNavigate();
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('view-only');
  const [newUserCanViewPasswords, setNewUserCanViewPasswords] = useState(false);
  const [selectedRole, setSelectedRole] = useState('view-only');
  const [editingUser, setEditingUser] = useState(null);
  const [selectedEmoji, setSelectedEmoji] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterLastLogin, setFilterLastLogin] = useState('all');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [emojiSearchQuery, setEmojiSearchQuery] = useState('');
  const [emojiCategoryIndex, setEmojiCategoryIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [canViewPasswords, setCanViewPasswords] = useState(false);
  const [acknowledgeSensitiveData, setAcknowledgeSensitiveData] = useState(false);
  const [sensitiveDataInfoAnchorEl, setSensitiveDataInfoAnchorEl] = useState(null);
  

  // Filter users based on search, role, and last login
  const filteredUsers = authorizedUsers.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    
    // Add last login filtering
    let matchesLastLogin = true;
    const now = new Date();
    const oneDay = 24 * 60 * 60 * 1000;
    const oneWeek = 7 * oneDay;
    const oneMonth = 30 * oneDay;

    if (filterLastLogin !== 'all') {
      if (filterLastLogin === 'never' && user.lastLoginAt) {
        matchesLastLogin = false;
      } else if (filterLastLogin === 'today' && (!user.lastLoginAt || now - user.lastLoginAt.toDate() > oneDay)) {
        matchesLastLogin = false;
      } else if (filterLastLogin === 'week' && (!user.lastLoginAt || now - user.lastLoginAt.toDate() > oneWeek)) {
        matchesLastLogin = false;
      } else if (filterLastLogin === 'month' && (!user.lastLoginAt || now - user.lastLoginAt.toDate() > oneMonth)) {
        matchesLastLogin = false;
      }
    }

    return matchesSearch && matchesRole && matchesLastLogin;
  });

  // Get current page users
  const currentPageUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );


  const loadAuthorizedUsers = useCallback(async () => {
    try {
      setLoading(true);
      if (!user || user.role !== 'admin') {
        throw new Error('Insufficient permissions');
      }

      console.log('Loading authorized users...');
      const querySnapshot = await getDocs(collection(db, 'authorized_users'));
      const authorizedUsersData = [];
      
      // Get all authorized users
      querySnapshot.forEach((doc) => {
        authorizedUsersData.push({
          email: doc.id,
          ...doc.data()
        });
      });

      // Fetch corresponding user data for login timestamps
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const userLoginData = {};
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.email) {
          userLoginData[userData.email] = {
            lastLoginAt: userData.lastLoginAt || null
          };
        }
      });

      // Combine the data
      const combinedUsers = authorizedUsersData.map(authUser => ({
        ...authUser,
        ...userLoginData[authUser.email],
        lastLoginAt: userLoginData[authUser.email]?.lastLoginAt || null
      }));

      console.log('Loaded users:', combinedUsers);
      setAuthorizedUsers(combinedUsers);
    } catch (error) {
      console.error('Error loading authorized users:', error);
      setSnackbar({
        open: true,
        message: error.message === 'Insufficient permissions' 
          ? 'You need admin privileges to view users' 
          : 'Error loading users',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadAuthorizedUsers();
    } else {
      setSnackbar({
        open: true,
        message: 'You need admin privileges to access this page',
        severity: 'error'
      });
    }
  }, [user, loadAuthorizedUsers]);

  // Use effect to handle admin role changes
  useEffect(() => {
    // Admins always have access to password fields
    if (selectedRole === 'admin') {
      setCanViewPasswords(true);
    }
  }, [selectedRole]);

  // Same for new user dialog
  useEffect(() => {
    // Admins always have access to password fields
    if (newUserRole === 'admin') {
      setNewUserCanViewPasswords(true);
    }
  }, [newUserRole]);

  const handleAddUser = async () => {
    try {
      if (!newUserEmail || !newUserEmail.includes('@')) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid email address',
          severity: 'error'
        });
        return;
      }

      // Prepare permissions array
      const permissions = [];
      if (newUserCanViewPasswords) {
        permissions.push('view-passwords');
      }

      // Add user to authorized_users collection
      await setDoc(doc(db, 'authorized_users', newUserEmail), {
        email: newUserEmail,
        role: newUserRole,
        permissions: permissions,
        authorized: true,
        authorizedBy: user.email,
        authorizedAt: new Date()
      });

      setOpenAddDialog(false);
      setNewUserEmail('');
      setNewUserRole('view-only');
      setNewUserCanViewPasswords(false);
      setAcknowledgeSensitiveData(false);
      loadAuthorizedUsers();

      setSnackbar({
        open: true,
        message: 'User added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding user:', error);
      setSnackbar({
        open: true,
        message: 'Error adding user',
        severity: 'error'
      });
    }
  };

  const handleDeleteUser = async (userEmail) => {
    setUserToDelete(userEmail);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      if (!userToDelete) return;

      // Delete from authorized_users collection
      await deleteDoc(doc(db, 'authorized_users', userToDelete));

      // Find and delete from users collection
      const usersQuery = query(collection(db, 'users'), where('email', '==', userToDelete));
      const userSnapshot = await getDocs(usersQuery);
      
      const batch = writeBatch(db);
      userSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();

      loadAuthorizedUsers();
      setOpenDeleteDialog(false);
      setUserToDelete(null);
      
      setSnackbar({
        open: true,
        message: 'User removed successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing user:', error);
      setSnackbar({
        open: true,
        message: 'Error removing user: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setSelectedRole(userToEdit.role);
    setSelectedEmoji(userToEdit.emoji || '');
    setCanViewPasswords(userToEdit.permissions?.includes('view-passwords') || false);
    setOpenEditDialog(true);
  };

  const handleUpdateRole = async () => {
    try {
      // Prepare permissions array
      const permissions = [];
      if (canViewPasswords) {
        permissions.push('view-passwords');
      }
      
      // Update authorized_users collection
      await updateDoc(doc(db, 'authorized_users', editingUser.email), {
        role: selectedRole,
        emoji: selectedEmoji,
        permissions: permissions,
        updatedBy: user.email,
        updatedAt: new Date()
      });

      // Update users collection
      const usersQuery = query(collection(db, 'users'), where('email', '==', editingUser.email));
      const userSnapshot = await getDocs(usersQuery);
      
      if (!userSnapshot.empty) {
        const userDoc = userSnapshot.docs[0];
        await updateDoc(doc(db, 'users', userDoc.id), {
          role: selectedRole,
          emoji: selectedEmoji,
          permissions: permissions,
          updatedBy: user.email,
          updatedAt: new Date()
        });
      }

      setOpenEditDialog(false);
      setEditingUser(null);
      loadAuthorizedUsers();

      setSnackbar({
        open: true,
        message: 'User updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating user:', error);
      setSnackbar({
        open: true,
        message: 'Error updating user',
        severity: 'error'
      });
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'SOS':
        return 'success';
      case 'editor':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Replace the handleEmojiButtonClick function
  const handleEmojiButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
    setShowEmojiPicker(true);
  };

  // Replace the handleCloseEmojiPicker function
  const handleCloseEmojiPicker = () => {
    setAnchorEl(null);
    setShowEmojiPicker(false);
  };

  // Replace the handleEmojiSelect function
  const handleEmojiSelect = (emoji) => {
    // Play confetti animation
    const colors = [
      '#FF4D4F', // Primary color
      '#FFB3B3', // Light red
      '#FF8C94', // Pink
      '#FFC53D', // Gold
      '#FFE58F', // Light gold
      '#FFD666', // Yellow
    ];
    
    // Create a fun confetti burst
    confetti({
      particleCount: 60,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors,
      shapes: ['circle', 'square'],
      scalar: 0.75
    });
    
    // Set the selected emoji
    setSelectedEmoji(emoji);
    handleCloseEmojiPicker();
  };

  // Add a function to remove the selected emoji
  const handleRemoveEmoji = () => {
    setSelectedEmoji('');
    
    // Show a small notification
    setSnackbar({
      open: true,
      message: 'Emoji removed',
      severity: 'info'
    });
  };

  // Filter emojis based on search query (searches by name and slug across all categories)
  const getFilteredEmojis = () => {
    const current = emojiCategories[emojiCategoryIndex]?.emojis || [];
    if (!emojiSearchQuery.trim()) {
      return current.map((e) => (typeof e === 'string' ? e : e.emoji));
    }
    const query = emojiSearchQuery.toLowerCase();
    const allEmojis = emojiCategories.flatMap((category) => category.emojis);
    return allEmojis
      .filter(
        (e) =>
          (e.name && e.name.toLowerCase().includes(query)) ||
          (e.slug && e.slug.toLowerCase().replace(/_/g, ' ').includes(query))
      )
      .map((e) => e.emoji);
  };

  // Handle emoji category change
  const handleEmojiCategoryChange = (event, newValue) => {
    setEmojiCategoryIndex(newValue);
    setEmojiSearchQuery(''); // Clear search when changing category
  };

  // Replace the renderEmojiPicker function
  const renderEmojiPicker = () => (
    <Popover
      open={showEmojiPicker}
      anchorEl={anchorEl}
      onClose={handleCloseEmojiPicker}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          p: 2,
          width: 350,
          borderRadius: 2,
          boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
          border: '1px solid',
          borderColor: 'divider',
        }
      }}
      disablePortal={false}
      container={document.body}
    >
      {/* Search bar */}
      <TextField
        placeholder="Search emojis..."
        size="small"
        fullWidth
        value={emojiSearchQuery}
        onChange={(e) => setEmojiSearchQuery(e.target.value)}
        sx={{ mb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      
      {/* Category tabs - only show when not searching */}
      {!emojiSearchQuery && (
        <Tabs
          value={emojiCategoryIndex}
          onChange={handleEmojiCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            mb: 1,
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              minWidth: 40,
              p: 0
            }
          }}
        >
          {emojiCategories.map((category, index) => (
            <Tab 
              key={index} 
              icon={category.icon} 
              aria-label={category.name}
              sx={{
                '&.Mui-selected': {
                  color: 'primary.main'
                }
              }}
            />
          ))}
        </Tabs>
      )}
      
      {/* Emoji grid */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 1,
        maxHeight: 300, // Increased height
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme => alpha(theme.palette.primary.main, 0.2),
          borderRadius: '20px',
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
          transition: 'background 0.2s ease',
          '&:hover': {
            background: theme => alpha(theme.palette.primary.main, 0.3),
          }
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          border: 'none',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: theme => `${alpha(theme.palette.primary.main, 0.2)} transparent`,
        '&:hover': {
          '&::-webkit-scrollbar-thumb': {
            background: theme => alpha(theme.palette.primary.main, 0.3),
          }
        }
      }}>
        {getFilteredEmojis().map((emoji, index) => (
          <Box 
            key={index}
            onClick={() => handleEmojiSelect(emoji)}
            sx={{ 
              fontSize: '1.5rem',
              cursor: 'pointer',
              textAlign: 'center',
              p: 0.5,
              borderRadius: 1,
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: alpha('#FF4D4F', 0.1),
                transform: 'scale(1.2)',
              }
            }}
          >
            {emoji}
          </Box>
        ))}
        
        {/* Show message when no emojis match search */}
        {emojiSearchQuery && getFilteredEmojis().length === 0 && (
          <Box sx={{ 
            gridColumn: 'span 8', 
            textAlign: 'center', 
            py: 4,
            color: 'text.secondary'
          }}>
            No emojis match your search
          </Box>
        )}
      </Box>
      
      {/* Category name */}
      {!emojiSearchQuery && (
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mt: 1,
            color: 'text.secondary',
            fontWeight: 500
          }}
        >
          {emojiCategories[emojiCategoryIndex].name}
        </Typography>
      )}
    </Popover>
  );

  // Check for admin access after all hooks
  if (user?.role !== 'admin') {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 149px)', // Updated for header height
        p: 3,
        textAlign: 'center'
      }}>
        <Typography 
          variant="h4" 
          color="error"
          sx={{ 
            fontFamily: 'StrawberrySansDisplay',
            fontWeight: 400,
            mb: 2,
            letterSpacing: '-0.02em'
          }}
        >
          Access Denied
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ 
            maxWidth: 400,
            fontFamily: 'StrawberrySansDisplay'
          }}
        >
          You don't have permission to access this page. Please contact an administrator for assistance.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      position: 'relative',
      minHeight: 'calc(100vh - 149px)', // Updated for header height
      maxWidth: '1400px',
      margin: '0 auto',
      p: { xs: 2, md: 4 },
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/')} 
            sx={{
              color: 'text.secondary',
              bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
              '&:hover': {
                color: 'primary.main',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
              },
              borderRadius: 2
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontFamily: 'StrawberrySansDisplay',
                fontWeight: 400,
                mb: 1,
                color: 'text.primary',
                fontSize: { xs: '1.5rem', md: '2rem' },
                letterSpacing: '-0.02em'
              }}
            >
              User Management
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                opacity: 0.8,
                fontFamily: 'StrawberrySansDisplay'
              }}
            >
              Manage user access and permissions
            </Typography>
          </Box>
        </Box>

        {/* Search and Filters */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <Box sx={{ 
            display: 'flex',
            gap: 1,
            flex: { xs: '1', md: 'initial' }
          }}>
            <TextField
              size="small"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }
              }}
            />
            <FormControl size="small">
              <Select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                sx={{
                  minWidth: 120,
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }}
              >
                <MenuItem value="all">All Roles</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="SOS">SOS</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="view-only">View Only</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small">
              <Select
                value={filterLastLogin}
                onChange={(e) => setFilterLastLogin(e.target.value)}
                sx={{
                  minWidth: 140,
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }}
              >
                <MenuItem value="all">All Login Times</MenuItem>
                <MenuItem value="never">Never Logged In</MenuItem>
                <MenuItem value="today">Last 24 Hours</MenuItem>
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            onClick={() => setOpenAddDialog(true)}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                boxShadow: 'none',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.8)
              }
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        pr: 2,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme => alpha(theme.palette.primary.main, 0.1),
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: theme => alpha(theme.palette.primary.main, 0.2),
        },
      }}>
        {/* Table */}
        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{ 
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            '& .MuiTableCell-root': {
              borderColor: theme => alpha(theme.palette.divider, 0.08),
              py: 2.5,
              px: 3,
              transition: 'all 0.2s ease',
              '&:first-of-type': {
                pl: 4
              },
              '&:last-of-type': {
                pr: 4
              }
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '0.875rem',
              letterSpacing: '0.02em',
              fontFamily: 'StrawberrySansDisplay',
              borderBottom: '2px solid',
              borderColor: theme => alpha(theme.palette.divider, 0.08),
              py: 2
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
                '& .MuiTableCell-root': {
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
                }
              }
            },
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: theme => alpha(theme.palette.primary.main, 0.2),
              boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.05)}`
            }
          }}
        >
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={40} />
              <Typography 
                color="text.secondary"
                sx={{ 
                  fontFamily: 'StrawberrySansDisplay',
                  opacity: 0.8
                }}
              >
                Loading users...
              </Typography>
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Added By</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Emoji</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentPageUsers.length === 0 ? (
                    <TableRow>
                      <TableCell 
                        colSpan={7} 
                        align="center"
                        sx={{ 
                          py: 8,
                          color: 'text.secondary',
                          fontFamily: 'StrawberrySansDisplay'
                        }}
                      >
                        {searchQuery || filterRole !== 'all' || filterLastLogin !== 'all' ? (
                          <>No users match your search criteria</>
                        ) : (
                          <>No users found</>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentPageUsers.map((user) => (
                      <TableRow 
                        key={user.email}
                        sx={{
                          '&:hover': {
                            '& .action-buttons': {
                              opacity: 1
                            }
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                fontFamily: 'StrawberrySansDisplay',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.12)
                                }
                              }}
                            >
                              {user.email[0].toUpperCase()}
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                                  {user.email}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                            color={getRoleColor(user.role)}
                            size="small"
                            sx={{ 
                              borderRadius: 1,
                              fontWeight: 500,
                              px: 1.5,
                              py: 0.5,
                              fontFamily: 'StrawberrySansDisplay',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography 
                            sx={{ 
                              fontFamily: 'StrawberrySansDisplay',
                              color: 'text.secondary'
                            }}
                          >
                            {user.addedBy}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            sx={{ 
                              fontFamily: 'StrawberrySansDisplay'
                            }}
                          >
                            {user.lastLoginAt ? user.lastLoginAt.toDate().toLocaleString() : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {user.emoji ? (
                            <Box 
                              sx={{ 
                                fontSize: '1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 36,
                                height: 36,
                                borderRadius: '10px',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                                }
                              }}
                            >
                              {user.emoji}
                            </Box>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontFamily: 'StrawberrySansDisplay' }}
                            >
                              None
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box 
                            className="action-buttons"
                            sx={{ 
                              display: 'flex', 
                              gap: 1, 
                              justifyContent: 'flex-end',
                              opacity: 0.6,
                              transition: 'opacity 0.2s ease',
                            }}
                          >
                            <Tooltip title="Edit User">
                              <IconButton
                                size="small"
                                onClick={() => handleEditUser(user)}
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: 'primary.main',
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove User">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(user.email)}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': { 
                                    color: 'error.dark',
                                    bgcolor: theme => alpha(theme.palette.error.main, 0.08)
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  borderTop: '1px solid',
                  borderColor: theme => alpha(theme.palette.divider, 0.08),
                  '& .MuiTablePagination-select': {
                    fontFamily: 'StrawberrySansDisplay'
                  },
                  '& .MuiTablePagination-displayedRows': {
                    fontFamily: 'StrawberrySansDisplay'
                  }
                }}
              />
            </>
          )}
        </TableContainer>
      </Box>

      {/* Add User Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={() => {
          setOpenAddDialog(false);
          setAcknowledgeSensitiveData(false);
          setSensitiveDataInfoAnchorEl(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? `0 24px 48px ${alpha(theme.palette.common.black, 0.4)}`
                : `0 24px 48px ${alpha(theme.palette.primary.main, 0.08)}`,
          },
        }}
      >
        {/* Header with identity (email + emoji) */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 60%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 60%)`,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <PersonOutlineIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontFamily: 'StrawberrySansDisplay',
                letterSpacing: '-0.02em',
              }}
            >
              Add new user
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box
              onClick={handleEmojiButtonClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: 2,
                border: '2px dashed',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
                cursor: 'pointer',
                fontSize: '1.75rem',
                lineHeight: 1,
                pt: '5px',
                transition: 'all 0.25s ease',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                flexShrink: 0,
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'scale(1.04)',
                  boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                },
                '& > span': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                },
              }}
            >
              <span>{selectedEmoji || '😀'}</span>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                label="Email address"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    '& fieldset': {
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                    },
                  },
                  '& .MuiInputLabel-root': { fontFamily: 'StrawberrySansDisplay' },
                  '& .MuiInputBase-input': { fontFamily: 'StrawberrySansDisplay' },
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                {selectedEmoji && (
                  <Tooltip title="Remove emoji">
                    <IconButton
                      size="small"
                      onClick={handleRemoveEmoji}
                      sx={{
                        color: 'error.main',
                        bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                        '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.2) },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                  Tap emoji to change
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 600,
                  letterSpacing: 1,
                  color: 'text.secondary',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                Role & permissions
              </Typography>
              <FormControl fullWidth size="medium" sx={{ mb: 2 }}>
                <InputLabel sx={{ fontFamily: 'StrawberrySansDisplay' }}>Role</InputLabel>
                <Select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  label="Role"
                  sx={{
                    borderRadius: 2,
                    fontFamily: 'StrawberrySansDisplay',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                    },
                  }}
                >
                  <MenuItem value="view-only" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                    View Only
                  </MenuItem>
                  <MenuItem value="editor" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                    Editor
                  </MenuItem>
                  <MenuItem value="SOS" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                    SOS
                  </MenuItem>
                  <MenuItem value="admin" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                    Admin
                  </MenuItem>
                </Select>
              </FormControl>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newUserCanViewPasswords || newUserRole === 'admin'}
                      onChange={(e) => setNewUserCanViewPasswords(e.target.checked)}
                      disabled={newUserRole === 'admin'}
                      sx={{
                        color: (theme) => alpha(theme.palette.primary.main, 0.7),
                        '&.Mui-checked': { color: 'primary.main' },
                        '&.Mui-disabled': { color: 'primary.main', opacity: 0.8 },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'StrawberrySansDisplay',
                        color: newUserRole === 'admin' ? 'text.secondary' : 'text.primary',
                      }}
                    >
                      Can view password fields
                      {newUserRole === 'admin' && ' (always on for admins)'}
                    </Typography>
                  }
                />
              </Box>

              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acknowledgeSensitiveData}
                      onChange={(e) => setAcknowledgeSensitiveData(e.target.checked)}
                      sx={{
                        color: (theme) => alpha(theme.palette.primary.main, 0.7),
                        '&.Mui-checked': { color: 'primary.main' },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                        I acknowledge this user may access sensitive data
                      </Typography>
                      <Tooltip title="Requirements for access">
                        <IconButton
                          size="small"
                          onClick={(e) => setSensitiveDataInfoAnchorEl(e.currentTarget)}
                          sx={{
                            color: 'text.secondary',
                            p: 0.25,
                            '&:hover': {
                              color: 'primary.main',
                              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                            },
                          }}
                          aria-describedby={sensitiveDataInfoAnchorEl ? 'sensitive-data-info-popover' : undefined}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
                <Popover
                  id="sensitive-data-info-popover"
                  open={Boolean(sensitiveDataInfoAnchorEl)}
                  anchorEl={sensitiveDataInfoAnchorEl}
                  onClose={() => setSensitiveDataInfoAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  PaperProps={{
                    sx: {
                      p: 2,
                      maxWidth: 320,
                      borderRadius: 2,
                      boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                    },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontFamily: 'StrawberrySansDisplay', fontWeight: 600, mb: 1.5 }}>
                    Requirements for access
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1 } }}>
                    <Typography component="li" variant="body2" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                      The user must have a legitimate need to access the underlying data.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                      The user must benefit from using the application (e.g. improved work efficiency, better coordination).
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                      Access should be limited to what is necessary for their role and responsibilities.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                      Sensitive data must not be shared or used outside of approved purposes.
                    </Typography>
                  </Box>
                </Popover>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : 'grey.50'),
          }}
        >
          <Button
            onClick={() => {
              setOpenAddDialog(false);
              setAcknowledgeSensitiveData(false);
              setSensitiveDataInfoAnchorEl(null);
            }}
            sx={{
              color: 'text.secondary',
              fontFamily: 'StrawberrySansDisplay',
              textTransform: 'none',
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.black, 0.04) },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={!newUserEmail || !newUserEmail.includes('@') || !acknowledgeSensitiveData}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              fontFamily: 'StrawberrySansDisplay',
              boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
              },
            }}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? `0 24px 48px ${alpha(theme.palette.common.black, 0.4)}`
                : `0 24px 48px ${alpha(theme.palette.primary.main, 0.08)}`,
          },
        }}
      >
        {/* Header with user identity */}
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 60%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 60%)`,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <PersonOutlineIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontFamily: 'StrawberrySansDisplay',
                letterSpacing: '-0.02em',
              }}
            >
              Edit user
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box
              onClick={handleEmojiButtonClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: 2,
                border: '2px dashed',
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
                cursor: 'pointer',
                fontSize: '1.75rem',
                lineHeight: 1,
                pt: '5px',
                transition: 'all 0.25s ease',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                flexShrink: 0,
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'scale(1.04)',
                  boxShadow: (theme) => `0 6px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                },
                // Inner wrapper forces emoji to center (fonts often give emoji uneven baseline)
                '& > span': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                },
              }}
            >
              <span>{selectedEmoji || '😀'}</span>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 500,
                  wordBreak: 'break-all',
                }}
              >
                {editingUser?.email}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                {selectedEmoji && (
                  <Tooltip title="Remove emoji">
                    <IconButton
                      size="small"
                      onClick={handleRemoveEmoji}
                      sx={{
                        color: 'error.main',
                        bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
                        '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.2) },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                  Tap emoji to change
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Role & permissions section */}
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontFamily: 'StrawberrySansDisplay',
                  fontWeight: 600,
                  letterSpacing: 1,
                  color: 'text.secondary',
                  display: 'block',
                  mb: 1.5,
                }}
              >
                Role & permissions
              </Typography>
              <FormControl fullWidth size="medium" sx={{ mb: 2 }}>
                <InputLabel sx={{ fontFamily: 'StrawberrySansDisplay' }}>Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Role"
                  sx={{
                    borderRadius: 2,
                    fontFamily: 'StrawberrySansDisplay',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                    },
                  }}
                >
                  <MenuItem value="view-only" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                    View Only
                  </MenuItem>
                  <MenuItem value="editor" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                    Editor
                  </MenuItem>
                  <MenuItem value="SOS" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                    SOS
                  </MenuItem>
                  <MenuItem value="admin" sx={{ fontFamily: 'StrawberrySansDisplay' }}>
                    Admin
                  </MenuItem>
                </Select>
              </FormControl>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  border: '1px solid',
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={canViewPasswords || selectedRole === 'admin'}
                      onChange={(e) => setCanViewPasswords(e.target.checked)}
                      disabled={selectedRole === 'admin'}
                      sx={{
                        color: (theme) => alpha(theme.palette.primary.main, 0.7),
                        '&.Mui-checked': { color: 'primary.main' },
                        '&.Mui-disabled': { color: 'primary.main', opacity: 0.8 },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'StrawberrySansDisplay',
                        color: selectedRole === 'admin' ? 'text.secondary' : 'text.primary',
                      }}
                    >
                      Can view password fields
                      {selectedRole === 'admin' && ' (always on for admins)'}
                    </Typography>
                  }
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : 'grey.50'),
          }}
        >
          <Button
            onClick={() => setOpenEditDialog(false)}
            sx={{
              color: 'text.secondary',
              fontFamily: 'StrawberrySansDisplay',
              textTransform: 'none',
              '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.black, 0.04) },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              fontFamily: 'StrawberrySansDisplay',
              boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
              },
            }}
          >
            Update role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setUserToDelete(null);
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              borderColor: theme => alpha(theme.palette.primary.main, 0.2),
              boxShadow: theme => `0 4px 20px ${alpha(theme.palette.primary.main, 0.05)}`
            },
            transition: 'all 0.2s ease'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography 
            variant="h6" 
            component="div"
            sx={{ 
              fontWeight: 600,
              fontFamily: 'StrawberrySansDisplay',
              letterSpacing: '-0.02em',
              color: 'error.main'
            }}
          >
            Confirm User Removal
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400 }}>
            <Typography sx={{ fontFamily: 'StrawberrySansDisplay' }}>
              Are you sure you want to remove this user?
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: theme => alpha(theme.palette.error.main, 0.05),
                borderRadius: 2,
                border: '1px solid',
                borderColor: theme => alpha(theme.palette.error.main, 0.1)
              }}
            >
              <Typography 
                sx={{ 
                  fontFamily: 'StrawberrySansDisplay',
                  color: 'error.main',
                  fontWeight: 500
                }}
              >
                {userToDelete}
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                fontFamily: 'StrawberrySansDisplay',
                mt: 1
              }}
            >
              This action will remove the user's access and all associated data. This cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 2 }}>
          <Button 
            onClick={() => {
              setOpenDeleteDialog(false);
              setUserToDelete(null);
            }}
            sx={{
              color: 'text.secondary',
              fontFamily: 'StrawberrySansDisplay',
              '&:hover': {
                bgcolor: alpha('#000', 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              fontFamily: 'StrawberrySansDisplay',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}
          >
            Remove User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 2,
            alignItems: 'center',
            fontFamily: 'StrawberrySansDisplay'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {renderEmojiPicker()}
    </Box>
  );
};

export default UserManagement; 